import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ItBacklogItem, 
  PRIORITY_CONFIG, 
  CATEGORY_CONFIG 
} from '@/types/itBacklog';
import { Clock, Calendar, User, Link2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface BacklogCardProps {
  item: ItBacklogItem;
  onClick: () => void;
  isDragging?: boolean;
}

export function BacklogCard({ item, onClick, isDragging }: BacklogCardProps) {
  const priority = PRIORITY_CONFIG[item.priority];
  const category = CATEGORY_CONFIG[item.category];
  
  const requesterName = item.requester?.display_name || 
    (item.requester?.first_name && item.requester?.last_name 
      ? `${item.requester.first_name} ${item.requester.last_name}` 
      : null);
  
  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-all border-l-4 ${
        item.priority === 'critical' ? 'border-l-red-500' :
        item.priority === 'urgent' ? 'border-l-orange-500' :
        item.priority === 'high' ? 'border-l-yellow-500' :
        'border-l-transparent'
      } ${isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-3 space-y-2">
        {/* Header with priority and category */}
        <div className="flex items-center justify-between gap-2">
          <Badge 
            variant="outline" 
            className={`text-xs ${priority.color}`}
          >
            {priority.icon} {priority.label}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {category.icon} {category.label}
          </Badge>
        </div>
        
        {/* Title */}
        <h4 className="font-medium text-sm line-clamp-2 leading-tight">
          {item.title}
        </h4>
        
        {/* Description preview */}
        {item.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">
            {item.description}
          </p>
        )}
        
        {/* Module tag */}
        {item.module && (
          <Badge variant="outline" className="text-xs">
            {item.module}
          </Badge>
        )}
        
        {/* Footer info */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground pt-1 border-t">
          {item.estimated_hours && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {item.estimated_hours}h
            </span>
          )}
          
          {item.due_date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {format(parseISO(item.due_date), "dd/MM", { locale: ptBR })}
            </span>
          )}
          
          {requesterName && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {requesterName.split(' ')[0]}
            </span>
          )}
          
          {item.bug_report && (
            <span className="flex items-center gap-1 text-blue-500">
              <Link2 className="h-3 w-3" />
              Bug
            </span>
          )}
        </div>
        
        {/* Blockers warning */}
        {item.blockers && (
          <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-1.5 rounded line-clamp-1">
            ⚠️ {item.blockers.length > 35 
              ? item.blockers.substring(0, 35) + '...' 
              : item.blockers}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
